/**
 * Root router and global providers (React Query, MUI theme, auth-gated shell).
 *
 * Where: mounted from `main.tsx` on `#root`.
 * Uses: `ProtectedRoute`, `DashboardLayout`, and all primary page components under `/`.
 * Behavior: shared `QueryClient` (no refetch on focus, limited retries); maps routes to features (inbox, leads, teams, admin).
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { theme } from '@/theme';
import { Login } from '@/pages/Login';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Dashboard } from '@/pages/Dashboard';
import { Teams } from '@/pages/Teams';
import { Experiments } from '@/pages/Experiments';
import { Inbox } from '@/pages/Inbox';
import { Leads } from '@/pages/Leads';
import { LeadDetails } from '@/pages/LeadDetails';
import { Users } from '@/pages/Users';
import { Profile } from '@/pages/Profile';
import { Settings } from '@/pages/Settings';
import { Logout } from '@/pages/Logout';
import { StreamDetails } from '@/pages/StreamDetails';
import { TeamDetails } from '@/pages/TeamDetails';
import { UserDetails } from '@/pages/UserDetails';
import { Notifications } from '@/pages/Notifications';
import { ReconnectLinkedIn } from '@/pages/ReconnectLinkedIn';
import { ConversationSignals } from '@/pages/ConversationSignals';
import { Commands } from '@/pages/Commands';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: false,
    },
  },
});

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reconnect-linkedin/:userId" element={<ReconnectLinkedIn />} />
      <Route path="/reconnect-linkedin" element={<ReconnectLinkedIn />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Dashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Dashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Teams />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/experiments"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Experiments />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inbox"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Inbox />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Leads />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leads/:leadId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <LeadDetails />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users/:userId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <UserDetails />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Users />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Profile />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Settings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams/:teamId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <TeamDetails />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:streamId"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <StreamDetails />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Notifications />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/commands"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Commands />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/conversation-signals"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ConversationSignals />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/logout" element={<Logout />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AppRoutes />
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
